// Charidy payment sources mapped to the org's own Admire payment methods
// (Settings > Billing in Admire). Defaults below match Admire's own default
// payment method list where one exists; buckets with no clear Admire default
// are left blank for the org to fill in.
//
// "offline" buckets are manually-entered donations, where the actual method
// (cash, check, Zelle, etc.) is picked by staff at data-entry time. The
// "Payment Gateways" side is built dynamically per org (see
// buildGatewayBuckets below), not listed here as fixed presets.
export const ADMIRE_OFFLINE_BUCKETS = [
  { key: "cash", label: "Cash", defaultName: "Cash" },
  { key: "check", label: "Check", defaultName: "Check" },
  {
    key: "credit_card",
    label: "Credit Card (manually entered)",
    defaultName: "Credit Card",
  },
  { key: "paypal", label: "PayPal", defaultName: "PayPal" },
  { key: "zelle", label: "Zelle", defaultName: "Zelle" },
  { key: "bank_transfer", label: "Bank Transfer", defaultName: "" },
  { key: "fund", label: "Donor Fund", defaultName: "Donor Fund" },
  { key: "charity_voucher", label: "Charity Voucher", defaultName: "" },
  {
    key: "other",
    label: "Other (fallback - also used for any gateway not mapped below)",
    defaultName: "Other",
  },
];

// Raw active-gateway identifiers (BankName-style, e.g. "paypal",
// "banquest-ach") already represented by a fixed bucket above, so they
// shouldn't also get their own row on the "Payment Gateways" side.
const GATEWAY_BUCKET_ALIASES = {
  paypal: "paypal",
  paypalv2: "paypal",
  "banquest-ach": "bank_transfer",
  "banquest-daf": "fund",
};

const prettifyGatewayLabel = (raw) =>
  raw
    .split("-")
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(" ");

// Maps a raw active-gateway identifier to the bucket key it should be mapped
// under, mirroring the backend's paymentMethodBucket exactly - so a mapping
// entered here always matches what donations actually get bucketed as.
export const gatewayBucketKey = (rawGateway) => {
  if (GATEWAY_BUCKET_ALIASES[rawGateway]) {
    return GATEWAY_BUCKET_ALIASES[rawGateway];
  }
  if (rawGateway.startsWith("banquest")) {
    return "banquest";
  }
  if (rawGateway.startsWith("cardknox")) {
    return "cardknox";
  }
  return rawGateway;
};

// Builds one row per distinct gateway family actually active on the org's
// account - Banquest/Cardknox variants (card, Apple Pay, terminal, etc.)
// collapse into a single row each, defaulting to "Credit Card" since Charidy
// can supply Admire a real token for those. Every other gateway gets its own
// row keyed by its raw identifier, defaulting to "Other". Gateways already
// covered by a fixed offline bucket (PayPal, Banquest ACH/DAF) are skipped.
export const buildGatewayBuckets = (activeGatewayIds) => {
  const buckets = new Map();

  activeGatewayIds.forEach((rawGateway) => {
    if (GATEWAY_BUCKET_ALIASES[rawGateway]) {
      return;
    }

    const key = gatewayBucketKey(rawGateway);
    if (buckets.has(key)) {
      return;
    }

    const isTokenized = key === "banquest" || key === "cardknox";
    buckets.set(key, {
      key,
      label: prettifyGatewayLabel(key),
      defaultName: isTokenized ? "Credit Card" : "Other",
    });
  });

  return Array.from(buckets.values());
};

export const getDefaultAdmirePaymentMethodMapping = (extraBuckets = []) => {
  const mapping = {};
  [...ADMIRE_OFFLINE_BUCKETS, ...extraBuckets].forEach((bucket) => {
    mapping[bucket.key] = { name: bucket.defaultName, id: "" };
  });
  return mapping;
};
